import { useEffect, useState } from "react";

import type { Light, VesselSpec, Viewpoint } from "@nav-games/nav-lights";

import { VesselSilhouette } from "./VesselSilhouette";

const COLORS: Record<string, { fill: string; glow: string }> = {
  white: { fill: "#ffffff", glow: "rgba(255, 255, 255, 0.85)" },
  red: { fill: "#d61414", glow: "rgba(214, 20, 20, 0.7)" },
  green: { fill: "#0c8a2c", glow: "rgba(12, 138, 44, 0.7)" },
  yellow: { fill: "#e0a800", glow: "rgba(224, 168, 0, 0.7)" },
};

interface DaytimeVesselFigureProps {
  vessel: VesselSpec;
  viewpoint?: Viewpoint;
  size?: number;
}

/// Daytime companion to `NavLightsFigure`. Same silhouette and same
/// light positions, but rendered against a daylight sky so the user
/// can read the vessel's outline + day shapes context. Lights are
/// still drawn but in their daylit colours (saturated, no halo) so
/// the user can see WHERE on the vessel each light sits. Lights
/// fitted but unlit during the day are still part of the equipment
/// — the daytime view teaches "this is where to look at night".
///
/// Used in Reference (side-by-side day/night) and as the reveal
/// frame after a Flashcards answer.
export function DaytimeVesselFigure({
  vessel,
  viewpoint = "headOn",
  size = 240,
}: DaytimeVesselFigureProps) {
  const lights = vessel.views[viewpoint] ?? vessel.views.headOn;

  const [flashOn, setFlashOn] = useState(true);
  useEffect(() => {
    const hasFlashing = lights.some((l) => l.characteristic === "flashing");
    if (!hasFlashing) return;
    const t = setInterval(() => setFlashOn((on) => !on), 500);
    return () => clearInterval(t);
  }, [lights]);

  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 100 100"
      aria-label={`Daytime view: ${vessel.name} (${viewpoint})`}
      role="img"
      style={{ background: "#d8eaf6", borderRadius: 8 }}
    >
      {/* Sky gradient */}
      <defs>
        <linearGradient id="day-sky" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bcdcef" />
          <stop offset="100%" stopColor="#e9f3fb" />
        </linearGradient>
        <linearGradient id="day-sea" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#7cabc6" />
          <stop offset="100%" stopColor="#4f8aa8" />
        </linearGradient>
      </defs>
      <rect x={0} y={0} width={100} height={80} fill="url(#day-sky)" />
      <rect x={0} y={80} width={100} height={20} fill="url(#day-sea)" />
      <line x1={0} y1={80} x2={100} y2={80} stroke="#3e6e88" strokeWidth={0.4} />

      <VesselSilhouette vesselId={vessel.id} viewpoint={viewpoint} mode="day" />

      {lights.map((light, i) => (
        <DayLightDot key={i} light={light} flashOn={flashOn} />
      ))}
    </svg>
  );
}

function DayLightDot({ light, flashOn }: { light: Light; flashOn: boolean }) {
  const colour = COLORS[light.color];
  const baseR = light.radius ?? 2.4;
  const r = light.farSide ? baseR * 0.7 : baseR;
  const dim = light.farSide ? 0.45 : 1;
  // Flashing lights at sea are usually only seen at night; in
  // daylight the visual cue is "fitted at this position" — draw at
  // full opacity always (no halo), even when the night animation
  // would have it off.
  const visible = light.characteristic === "flashing" ? flashOn : true;
  if (!colour) return null;
  return (
    <g>
      <circle
        cx={light.x}
        cy={light.y}
        r={r + 0.4}
        fill="#fff"
        opacity={dim}
      />
      <circle
        cx={light.x}
        cy={light.y}
        r={r}
        fill={colour.fill}
        opacity={visible ? dim : dim * 0.3}
      />
    </g>
  );
}
